// src/components/Widgets/TemplatesWidget.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import WidgetHeader from "../Common/WidgetHeader";

const makeFormatter = (currency = "AUD") => {
  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency });
  } catch {
    return { format: (v) => `A$${Number(v || 0).toFixed(2)}` };
  }
};

const TemplatesWidget = ({
  templates = [],
  onSelect = () => {},
  showCount = true,
  backTo = "/dashboard/templates",
  currency = "AUD",
}) => {
  const fmt = useMemo(() => makeFormatter(currency), [currency]);
  const navigate = useNavigate();

  return (
    // match other widgets: transparent wrapper, inherited text, full height
    <div className="rounded-lg p-4 h-full flex flex-col bg-transparent text-inherit">
      <WidgetHeader
        title="Templates"
        to={backTo}
        right={
          <div className="flex items-center gap-2">
            {showCount && <span className="text-sm opacity-60">({templates.length})</span>}
            <button
              onClick={() => navigate(backTo)}
              className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors"
              title="View all templates"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        }
      />

      {templates.length > 0 ? (
        <div className="space-y-3">
          {templates.slice(0, 3).map((tpl, idx) => (
            <div
              key={idx}
              onClick={() => onSelect(tpl)}
              className="border border-white/10 rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 transition cursor-pointer"
            >
              {/* Thumbnail */}
              {tpl.imageUrl ? (
                <div className="w-full h-50 bg-gradient-to-br from-white/10 to-white/5 relative">
                  <img
                    src={tpl.imageUrl}
                    alt={tpl.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-full h-24 bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center">
                  <span className="text-2xl opacity-30">📄</span>
                </div>
              )}
              {/* Info */}
              <div className="p-3">
                <p className="font-medium truncate text-sm">
                  {tpl.name || "Untitled"}
                </p>
                <p className="opacity-60 text-xs truncate mt-1">
                  {tpl.category || "Uncategorized"}
                </p>
              </div>
            </div>
          ))}
          {templates.length > 3 && (
            <p className="text-xs text-center opacity-60 pt-2">
              +{templates.length - 3} more
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm italic opacity-70">No saved templates yet.</p>
      )}
    </div>
  );
};

TemplatesWidget.propTypes = {
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      method: PropTypes.string,
      finalPrice: PropTypes.number,
      quantity: PropTypes.number,
    })
  ),
  onSelect: PropTypes.func,
  showCount: PropTypes.bool,
  backTo: PropTypes.string,
  currency: PropTypes.string,
};

export default TemplatesWidget;
